import { marked } from 'marked';
import { useMemo } from 'react';
import { ContentType, CONTENT_TYPE_LABELS } from '../../types';
import { CopyButton } from './CopyButton';
import { ExportMarkdown } from './ExportMarkdown';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';

interface ContentPreviewProps {
  type: ContentType;
  content: string;
  status: 'pending_approval' | 'approved' | 'needs_revision';
  weekSlug?: string;
  onApprove?: () => void;
  onRequestRevision?: () => void;
}

function BlogPreview({ content }: { content: string }) {
  const html = useMemo(() => {
    const articleEnd = content.indexOf('\n---\nMETA:');
    const markdownBody = articleEnd > -1 ? content.slice(0, articleEnd) : content;
    return marked.parse(markdownBody) as string;
  }, [content]);

  return (
    <div
      className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-falcon-navy prose-a:text-falcon-blue"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function SocialPreview({ content }: { content: string }) {
  const sections = content.split(/\n(?=LINKEDIN_COMPANY_\d|TWITTER_THREAD|GOOGLE_BUSINESS)/);
  return (
    <div className="space-y-4">
      {sections.map((section, i) => {
        const lines = section.trim().split('\n');
        const label = lines[0];
        const body = lines.slice(1).join('\n').trim();
        return (
          <div key={i} className="border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-falcon-blue mb-2 uppercase tracking-wide">
              {label}
            </p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap font-body">{body}</p>
          </div>
        );
      })}
    </div>
  );
}

function GraphicsPreview({ content }: { content: string }) {
  const html = useMemo(() => marked.parse(content) as string, [content]);
  return (
    <div
      className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-falcon-navy"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

const STATUS_BADGE: Record<string, { variant: 'warning' | 'success' | 'error'; label: string }> = {
  pending_approval: { variant: 'warning', label: 'Pending Approval' },
  approved: { variant: 'success', label: 'Approved' },
  needs_revision: { variant: 'error', label: 'Needs Revision' },
};

export function ContentPreview({
  type,
  content,
  status,
  weekSlug,
  onApprove,
  onRequestRevision,
}: ContentPreviewProps) {
  const badgeInfo = STATUS_BADGE[status];
  const filename = `${weekSlug ?? 'content'}-${type}`;

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-display font-semibold text-falcon-navy">
            {CONTENT_TYPE_LABELS[type]}
          </h3>
          <Badge variant={badgeInfo.variant}>{badgeInfo.label}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <CopyButton content={content} />
          <ExportMarkdown content={content} filename={filename} />
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4">
        {type === 'blog' && <BlogPreview content={content} />}
        {(type === 'social_posts' || type === 'linkedin_founder') && (
          <SocialPreview content={content} />
        )}
        {type === 'graphics_specs' && <GraphicsPreview content={content} />}
        {(type === 'topics' || type === 'outline') && (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: marked.parse(content) as string }}
          />
        )}
      </div>

      {status === 'pending_approval' && (onApprove || onRequestRevision) && (
        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          {onApprove && (
            <button
              onClick={onApprove}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
            >
              Approve
            </button>
          )}
          {onRequestRevision && (
            <button
              onClick={onRequestRevision}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Request Changes
            </button>
          )}
        </div>
      )}
    </Card>
  );
}
